import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  TrendingUp, Star, Target, Award, Users, Calendar,
  ChevronRight, FileText, Plus, Loader2, CheckCircle, X, Trash2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BEHAVIOR_LABELS = {
  integrity: 'Integrity',
  speed: 'Speed', 
  innovation: 'Innovation',
  simplicity: 'Simplicity',
  ownership: 'Ownership',
  transparency: 'Transparency'
};

const Performance = () => {
  const [summary, setSummary] = useState(null);
  const [myGoals, setMyGoals] = useState([]);
  const [subordinatesGoals, setSubordinatesGoals] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [subordinates, setSubordinates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-goals');
  
  // Modals
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  
  // Form state
  const [goalForm, setGoalForm] = useState({
    employee_id: '',
    title: '',
    description: '',
    review_period: '',
    priority: 'medium',
    due_date: '',
    kras: [{ title: '', description: '', weight: 1 }],
    kpis: [{ title: '', target: '', unit: '' }],
    behavior_expectations: {
      integrity: null,
      speed: null,
      innovation: null,
      simplicity: null,
      ownership: null,
      transparency: null
    }
  });
  
  const [reviewForm, setReviewForm] = useState({
    employee_id: '',
    review_type: 'quarterly',
    due_date: ''
  });
  
  const { user, getAuthHeaders, isOfficeAdmin, isSuperAdmin, currentOffice } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const isAdmin = isOfficeAdmin() || isSuperAdmin();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }

      const requests = [
        fetch(`${API_URL}/api/performance/goals/my-goals`, { headers }),
        fetch(`${API_URL}/api/performance/goals/subordinates`, { headers }),
        fetch(`${API_URL}/api/performance/reviews/pending`, { headers }),
        fetch(`${API_URL}/api/performance/summary`, { headers }),
        fetch(`${API_URL}/api/performance/subordinates`, { headers }),
      ];

      if (isAdmin) {
        requests.push(fetch(`${API_URL}/api/employees/`, { headers }));
      }

      const results = await Promise.all(requests);

      if (results[0].ok) setMyGoals(await results[0].json());
      if (results[1].ok) setSubordinatesGoals(await results[1].json());
      if (results[2].ok) setPendingReviews(await results[2].json());
      if (results[3].ok) setSummary(await results[3].json());
      if (results[4].ok) setSubordinates(await results[4].json());
      if (results[5]?.ok) setEmployees(await results[5].json());
    } catch (error) {
      console.error('Error fetching performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }
      
      // Filter out empty KRAs and KPIs
      const filteredKras = goalForm.kras.filter(k => k.title.trim());
      const filteredKpis = goalForm.kpis.filter(k => k.title.trim());
      
      // Filter out null behavior expectations
      const behaviorExp = {};
      Object.entries(goalForm.behavior_expectations).forEach(([key, val]) => {
        if (val !== null) behaviorExp[key] = val;
      });
      
      const payload = {
        ...goalForm,
        employee_id: goalForm.employee_id || user.id,
        kras: filteredKras,
        kpis: filteredKpis,
        behavior_expectations: Object.keys(behaviorExp).length > 0 ? behaviorExp : null
      };
      
      const response = await fetch(`${API_URL}/api/performance/goals`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setShowGoalModal(false);
        resetGoalForm();
        fetchData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create goal');
      }
    } catch (error) {
      console.error('Error creating goal:', error);
    }
  };

  const resetGoalForm = () => {
    setGoalForm({
      employee_id: '',
      title: '',
      description: '',
      review_period: '',
      priority: 'medium',
      due_date: '',
      kras: [{ title: '', description: '', weight: 1 }],
      kpis: [{ title: '', target: '', unit: '' }],
      behavior_expectations: {
        integrity: null, speed: null, innovation: null,
        simplicity: null, ownership: null, transparency: null
      }
    });
  };

  const handleCreateReview = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }
      
      const response = await fetch(`${API_URL}/api/performance/reviews`, {
        method: 'POST',
        headers,
        body: JSON.stringify(reviewForm)
      });
      
      if (response.ok) {
        setShowReviewModal(false);
        setReviewForm({ employee_id: '', review_type: 'quarterly', due_date: '' });
        fetchData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create review');
      }
    } catch (error) {
      console.error('Error creating review:', error);
    }
  };

  const handleUpdateGoalProgress = async (goalId, progress) => {
    try {
      const headers = getAuthHeaders();
      const status = progress >= 100 ? 'completed' : 'in_progress';
      
      await fetch(`${API_URL}/api/performance/goals/${goalId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ progress: Math.min(100, progress), status })
      });
      
      fetchData();
    } catch (error) {
      console.error('Error updating goal:', error);
    }
  };

  const addKRA = () => {
    setGoalForm({
      ...goalForm,
      kras: [...goalForm.kras, { title: '', description: '', weight: 1 }]
    });
  };

  const removeKRA = (index) => {
    setGoalForm({
      ...goalForm,
      kras: goalForm.kras.filter((_, i) => i !== index)
    });
  };

  const updateKRA = (index, field, value) => {
    const newKras = [...goalForm.kras];
    newKras[index][field] = value;
    setGoalForm({ ...goalForm, kras: newKras });
  };

  const addKPI = () => {
    setGoalForm({
      ...goalForm,
      kpis: [...goalForm.kpis, { title: '', target: '', unit: '' }]
    });
  };

  const removeKPI = (index) => {
    setGoalForm({
      ...goalForm,
      kpis: goalForm.kpis.filter((_, i) => i !== index)
    });
  };

  const updateKPI = (index, field, value) => {
    const newKpis = [...goalForm.kpis];
    newKpis[index][field] = value;
    setGoalForm({ ...goalForm, kpis: newKpis });
  };

  const updateBehaviorExpectation = (key, value) => {
    setGoalForm({
      ...goalForm,
      behavior_expectations: {
        ...goalForm.behavior_expectations,
        [key]: value === '' ? null : parseInt(value)
      }
    });
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-green-600';
    if (rating >= 3.5) return 'text-blue-600';
    if (rating >= 2.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusBadge = (status) => {
    const styles = {
      not_started: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || styles.not_started}>{status?.replace('_', ' ')}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-gray-100 text-gray-700',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[priority] || styles.medium}>{priority}</Badge>;
  };

  const renderGoalCard = (goal, isOwn = true) => (
    <div key={goal.id} className="p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-medium">
            {goal.employee_name?.split(' ').map(n => n[0]).join('') || '?'}
          </div>
          <div>
            <h3 className="font-semibold">{goal.title}</h3>
            <p className="text-sm text-gray-500">
              {goal.employee_name}
              {goal.review_period && <span className="ml-2 text-blue-600">• {goal.review_period}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getPriorityBadge(goal.priority)}
          {getStatusBadge(goal.status)}
        </div>
      </div>
      
      {goal.description && <p className="text-sm text-gray-600 mb-3">{goal.description}</p>}
      
      {/* KRAs */}
      {goal.kras?.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-500 mb-1">Key Result Areas:</p>
          <div className="flex flex-wrap gap-1">
            {goal.kras.map((kra, i) => (
              <Badge key={i} variant="outline" className="text-xs">{kra.title}</Badge>
            ))}
          </div>
        </div>
      )}
      
      {/* KPIs */}
      {goal.kpis?.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-500 mb-1">KPIs:</p>
          <div className="flex flex-wrap gap-1">
            {goal.kpis.map((kpi, i) => (
              <Badge key={i} variant="secondary" className="text-xs">
                {kpi.title}: {kpi.target}
              </Badge>
            ))}
          </div>
        </div>
      )}
      
      {/* Behavior Expectations */}
      {goal.behavior_expectations && (
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-500 mb-1">Behavior Expectations:</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(goal.behavior_expectations).map(([key, val]) => 
              val !== null && (
                <span key={key} className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">
                  {BEHAVIOR_LABELS[key]}: {val}/5
                </span>
              )
            )}
          </div>
        </div>
      )}
      
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Progress</span>
          <div className="flex items-center gap-2">
            <span className="font-medium">{goal.progress}%</span>
            {isOwn && goal.status !== 'completed' && (
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => handleUpdateGoalProgress(goal.id, goal.progress + 10)}>
                  +10%
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleUpdateGoalProgress(goal.id, 100)}>
                  <CheckCircle className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
        <Progress value={goal.progress} className="h-2" />
        {goal.due_date && (
          <p className="text-xs text-gray-500">Due: {new Date(goal.due_date).toLocaleDateString()}</p>
        )}
      </div>
    </div>
  );

  // Get assignable employees (subordinates for managers, all for admins)
  const getAssignableEmployees = () => {
    if (isAdmin && employees.length > 0) {
      return employees;
    }
    return subordinates;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Performance</h1>
          <p className="text-gray-600 dark:text-gray-400">
            {subordinates.length > 0 
              ? `Manage your goals and review your ${subordinates.length} team member${subordinates.length > 1 ? 's' : ''}`
              : 'Track your performance goals and reviews'
            }
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowGoalModal(true)} data-testid="create-goal-button">
            <Target className="h-4 w-4 mr-2" />
            Set Goal
          </Button>
          {(subordinates.length > 0 || isAdmin) && (
            <Button onClick={() => setShowReviewModal(true)} data-testid="create-review-button">
              <Plus className="h-4 w-4 mr-2" />
              Create Review
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avg. Rating</p>
                  <p className={`text-2xl font-bold ${summary.avg_rating ? getRatingColor(summary.avg_rating) : 'text-gray-400'}`}>
                    {summary.avg_rating || '-'}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <Star className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Goals Completed</p>
                  <p className="text-2xl font-bold">{summary.goals_completion_rate || 0}%</p>
                  <p className="text-xs text-gray-500">{summary.completed_goals}/{summary.total_goals}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Target className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending Reviews</p>
                  <p className="text-2xl font-bold">{summary.pending_reviews}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <FileText className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{subordinates.length > 0 ? 'Team Size' : 'Top Performers'}</p>
                  <p className="text-2xl font-bold">{subordinates.length > 0 ? subordinates.length : summary.top_performers}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  {subordinates.length > 0 ? <Users className="h-6 w-6 text-orange-600" /> : <Award className="h-6 w-6 text-orange-600" />}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="my-goals">My Goals ({myGoals.length})</TabsTrigger>
          {subordinates.length > 0 && (
            <TabsTrigger value="team-goals">Team Goals ({subordinatesGoals.length})</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="all-goals">All Goals</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="my-goals">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>My Performance Goals</CardTitle>
                  <Badge variant="outline">{myGoals.length} goals</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {myGoals.length > 0 ? (
                  <div className="space-y-4">
                    {myGoals.map((goal) => renderGoalCard(goal, true))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Target className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No goals set yet</p>
                    <Button variant="outline" className="mt-4" onClick={() => { setGoalForm({...goalForm, employee_id: user.id}); setShowGoalModal(true); }}>
                      Set Your First Goal
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Reviews */}
            <Card>
              <CardHeader>
                <CardTitle>Pending Reviews</CardTitle>
              </CardHeader>
              <CardContent>
                {pendingReviews.length > 0 ? (
                  <div className="space-y-4">
                    {pendingReviews.map((review) => (
                      <div key={review.id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium">{review.employee_name}</h4>
                          <Badge variant="outline">{review.review_type}</Badge>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <Badge className={
                            review.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            review.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {review.status}
                          </Badge>
                          {review.due_date && (
                            <span className="flex items-center gap-1 text-orange-600">
                              <Calendar className="h-3 w-3" />
                              {new Date(review.due_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No pending reviews</p>
                  </div>
                )}
                {(subordinates.length > 0 || isAdmin) && (
                  <Button variant="outline" className="w-full mt-4" onClick={() => setShowReviewModal(true)}>
                    Schedule Review
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {subordinates.length > 0 && (
          <TabsContent value="team-goals">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Team Performance Goals</CardTitle>
                  <Badge variant="outline">{subordinatesGoals.length} goals</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {subordinatesGoals.length > 0 ? (
                  <div className="space-y-4">
                    {subordinatesGoals.map((goal) => renderGoalCard(goal, false))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No team goals yet</p>
                    <Button variant="outline" className="mt-4" onClick={() => setShowGoalModal(true)}>
                      Set Goal for Team Member
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Create Goal Modal */}
      <Dialog open={showGoalModal} onOpenChange={setShowGoalModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Set Performance Goal</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Employee Selection */}
            {(subordinates.length > 0 || isAdmin) && (
              <div>
                <Label>Employee</Label>
                <Select value={goalForm.employee_id || 'self'} onValueChange={(v) => setGoalForm({...goalForm, employee_id: v === 'self' ? '' : v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Myself</SelectItem>
                    {getAssignableEmployees().map((emp) => (
                      <SelectItem key={emp.id || emp.user_id} value={emp.id || emp.user_id}>
                        {emp.first_name} {emp.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Goal Title *</Label>
                <Input value={goalForm.title} onChange={(e) => setGoalForm({...goalForm, title: e.target.value})} placeholder="e.g., Increase sales by 20%" />
              </div>
              <div>
                <Label>Review Period</Label>
                <Select value={goalForm.review_period} onValueChange={(v) => setGoalForm({...goalForm, review_period: v})}>
                  <SelectTrigger><SelectValue placeholder="Select period" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Q1 2026">Q1 2026</SelectItem>
                    <SelectItem value="Q2 2026">Q2 2026</SelectItem>
                    <SelectItem value="Q3 2026">Q3 2026</SelectItem>
                    <SelectItem value="Q4 2026">Q4 2026</SelectItem>
                    <SelectItem value="2026">Full Year 2026</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={goalForm.priority} onValueChange={(v) => setGoalForm({...goalForm, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date</Label>
                <Input type="date" value={goalForm.due_date} onChange={(e) => setGoalForm({...goalForm, due_date: e.target.value})} />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea value={goalForm.description} onChange={(e) => setGoalForm({...goalForm, description: e.target.value})} placeholder="Goal details..." rows={2} />
              </div>
            </div>

            {/* KRAs */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Key Result Areas (KRAs)</Label>
                <Button variant="outline" size="sm" onClick={addKRA}><Plus className="h-4 w-4 mr-1" />Add KRA</Button>
              </div>
              <div className="space-y-3">
                {goalForm.kras.map((kra, index) => (
                  <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <Input className="col-span-2" placeholder="KRA Title" value={kra.title} onChange={(e) => updateKRA(index, 'title', e.target.value)} />
                      <Input type="number" placeholder="Weight" value={kra.weight} onChange={(e) => updateKRA(index, 'weight', parseFloat(e.target.value) || 1)} min="0.1" step="0.1" />
                    </div>
                    {goalForm.kras.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeKRA(index)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* KPIs */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Key Performance Indicators (KPIs)</Label>
                <Button variant="outline" size="sm" onClick={addKPI}><Plus className="h-4 w-4 mr-1" />Add KPI</Button>
              </div>
              <div className="space-y-3">
                {goalForm.kpis.map((kpi, index) => (
                  <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <Input placeholder="KPI Title" value={kpi.title} onChange={(e) => updateKPI(index, 'title', e.target.value)} />
                      <Input placeholder="Target (e.g., 100)" value={kpi.target} onChange={(e) => updateKPI(index, 'target', e.target.value)} />
                      <Input placeholder="Unit (e.g., %)" value={kpi.unit} onChange={(e) => updateKPI(index, 'unit', e.target.value)} />
                    </div>
                    {goalForm.kpis.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeKPI(index)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Behavior Expectations */}
            <div>
              <Label className="text-base font-semibold mb-2 block">Behavior Expectations (1-5 Scale)</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(BEHAVIOR_LABELS).map(([key, label]) => (
                  <div key={key} className="flex flex-col">
                    <Label className="text-sm mb-1">{label}</Label>
                    <Select value={goalForm.behavior_expectations[key]?.toString() || 'not-set'} onValueChange={(v) => updateBehaviorExpectation(key, v === 'not-set' ? '' : v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not-set">Not Set</SelectItem>
                        <SelectItem value="1">1 - Needs Improvement</SelectItem>
                        <SelectItem value="2">2 - Below Expectations</SelectItem>
                        <SelectItem value="3">3 - Meets Expectations</SelectItem>
                        <SelectItem value="4">4 - Exceeds Expectations</SelectItem>
                        <SelectItem value="5">5 - Outstanding</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowGoalModal(false); resetGoalForm(); }}>Cancel</Button>
            <Button onClick={handleCreateGoal} disabled={!goalForm.title}>Create Goal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Performance Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Employee *</Label>
              <Select value={reviewForm.employee_id} onValueChange={(v) => setReviewForm({...reviewForm, employee_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {getAssignableEmployees().map((emp) => (
                    <SelectItem key={emp.id || emp.user_id} value={emp.id || emp.user_id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Review Type</Label>
              <Select value={reviewForm.review_type} onValueChange={(v) => setReviewForm({...reviewForm, review_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                  <SelectItem value="probation">Probation</SelectItem>
                  <SelectItem value="adhoc">Ad-hoc</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input type="date" value={reviewForm.due_date} onChange={(e) => setReviewForm({...reviewForm, due_date: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewModal(false)}>Cancel</Button>
            <Button onClick={handleCreateReview} disabled={!reviewForm.employee_id}>Schedule Review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Performance;
